/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { Col, Divider, Layout, Row, Spin } from 'antd';
import { Button } from '@/uiKit';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { IUploadedResponse } from '@/hooks/useS3';
import {
  deleteTemplateIdForRequest,
  getTemplateIdForRequest
} from '@/pages/Playgrounds/templateUtils';
import templates from '@/pages/Playgrounds/templates';
import { currentUserDetailsVar } from '@/graphql/cache/user';
import { useRunCtxV2 } from '@/provider/runv2';
import { AnalyticsEvents } from '@/analytics/events';
import { trackEvent } from '@/tracker';
import { openArchiveRunConfirmModal } from '@/utils/run';
import useALCFlag from '@/hooks/useALCFlag';
import {
  Request as RequestInterface,
  Worker,
  Procedure,
  ProcedureStage
} from '../../generated/graphql';
import RequestQuestion from './RequestQuestion';
import './Request.less';
import {
  getQuestionAnswer,
  isQuestionAnswered,
  isRequesArchived,
  isRequestHandled,
  verifyQuestionValues
} from '../../utils/request';
import {
  IQuestionConfig,
  IQuestionValues,
  QuestionInputType,
  RequestQuestionType
} from './interface';
import AppConstants from '../../utils/AppConstants';
import { useAppDispatch } from '../../stores/hooks';
import { resetAnnotations } from '../../stores/slices/annotation';
import { ISentence } from '../../pages/PlaygroundV3/components/Editor/editorInterface';
import useRequestMethods from '../../hooks/useRequestMethods';
import Gutter from '../layout/Gutter';

interface IRequestsProps {
  request: RequestInterface;
  worker: Worker;
  procedure?: Procedure;
  sentence?: ISentence;
  activeDocumentToken?: string;
  hideAnsweredQuestion: boolean;
  questionConfig: IQuestionConfig;
  onSuccess?: () => void;
  archive?: {
    onSuccess?: (archiveStatus: boolean) => void;
    hideArchiveButton?: boolean;
  };
}

function Request(props: IRequestsProps) {
  const {
    request,
    worker,
    procedure,
    sentence,
    activeDocumentToken,
    hideAnsweredQuestion,
    questionConfig,
    onSuccess,
    archive
  } = props;
  const { questions: _questions } = request;
  const { email } = useReactiveVar(currentUserDetailsVar);
  const { isALCEnabled } = useALCFlag();

  const dispatch = useAppDispatch();

  const [questionValuesMap, setQuestionValuesMap] = useState<
    Record<string, IQuestionValues>
  >({});
  const [allowSubmit, toggleAllowSubmit] = useState(true);

  const [loadingQuestionIds, setLoadingQuestionIds] = useState(new Set());

  const { answerQuestion } = useRequestMethods(request);

  const { toggleArchive } = useRunCtxV2();

  const pendingQuestions = _questions?.filter((question) => {
    return !isQuestionAnswered(question, request.answers!);
  });
  const questions = hideAnsweredQuestion ? pendingQuestions : _questions;

  useEffect(() => {
    return () => {
      dispatch(resetAnnotations());
    };
  }, [request.id]);

  const resolveQuestion = async (
    question: RequestQuestionType,
    values: IQuestionValues
  ) => {
    setLoadingQuestionIds((prevState) => prevState.add(question.id));
    const newQuestionValuesMap = { ...questionValuesMap };
    delete newQuestionValuesMap[question.id];
    setQuestionValuesMap(newQuestionValuesMap);

    answerQuestion(question, values, values.remember || false)
      .then(() => {
        if (onSuccess) {
          onSuccess();
          deleteTemplateIdForRequest(email!);
        }
        loadingQuestionIds.delete(question.id);
      })
      .catch(() => {
        loadingQuestionIds.delete(question.id);
      });
  };

  useEffect(() => {
    const selfServeSampleFileHandler = (data: {
      detail: {
        question: RequestQuestionType;
        uploadResult: IUploadedResponse[];
      };
    }) => {
      resolveQuestion(data.detail.question, {
        inputType: QuestionInputType.VALUE_UPLOAD,
        remember: false,
        upload: data.detail.uploadResult
      });
    };
    document.addEventListener(
      AppConstants.SELF_SERVE.FILE_UPLOAD_EVENT,
      selfServeSampleFileHandler as any
    );

    return () =>
      document.removeEventListener(
        AppConstants.SELF_SERVE.FILE_UPLOAD_EVENT,
        selfServeSampleFileHandler as any
      );
  }, []);

  useEffect(() => {
    if (isRequestHandled(request) || isRequesArchived(request)) {
      toggleAllowSubmit(false);
    }

    const templateId = getTemplateIdForRequest(email!);
    const currentTemplateSampleFile = templates
      .find((i) => `${i.id}` === templateId)
      ?.questions?.find(
        (i) => JSON.stringify(i?.stepPath) === JSON.stringify(request?.stepPath)
      );
    if (currentTemplateSampleFile) {
      toggleAllowSubmit(false);
    }

    const filteredQuestions = questions?.filter(
      (question: RequestQuestionType) => {
        const type = question.__typename;
        return (
          type === 'DomainNotIntegratedQuestion' ||
          type === 'MissingIntegrationCredentialQuestion'
        );
      }
    );
    if (filteredQuestions?.length !== 0) {
      toggleAllowSubmit(false);
    }
  }, [request, questions, email]);

  const onSubmit = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEvent(AnalyticsEvents.EXCEPTION_CENTER.ClickQuestionSubmit);
    Object.keys(questionValuesMap).forEach((key) => {
      if (!questionValuesMap[key].inputType) {
        questionValuesMap[key].inputType = QuestionInputType.NO_VALUE;
      }
    });
    return Object.keys(questionValuesMap)
      .filter((id) => verifyQuestionValues(questionValuesMap[id]))
      .forEach((id) => {
        resolveQuestion(
          questions!.find((q) => q.id === id)!,
          questionValuesMap[id]
        );
      });
  };

  const isOldPublishedWorkerWithoutStage =
    worker && !worker.stage && isALCEnabled && procedure?.id;

  const onValuesChange = (
    question: RequestQuestionType,
    allValues: IQuestionValues
  ) => {
    const tempValues = { ...allValues };
    // In case of draft run, set the draft to true, but user can add it to published as well
    if (allValues.remember && worker.stage === ProcedureStage.Draft) {
      tempValues.draft = true;
    }
    // All old workers are published, keeping backwards compatibility here
    // In case of published run, set the published to true, but user can add it to draft as well
    else if (
      allValues.remember &&
      (worker.stage === ProcedureStage.Published ||
        isOldPublishedWorkerWithoutStage)
    ) {
      tempValues.published = true;
    }
    // If remember is false reset the draft and published values
    else {
      tempValues.draft = false;
      tempValues.published = false;
    }

    setQuestionValuesMap((prevState) => {
      const shouldNotChangeValue =
        prevState[question.id]?.inputType === QuestionInputType.VALUE &&
        tempValues.inputType === QuestionInputType.VALUE &&
        Object.keys(prevState).includes('secret') &&
        tempValues?.secret !== prevState[question.id]?.secret;

      return {
        ...prevState,
        [question.id]: shouldNotChangeValue
          ? { ...tempValues, value: prevState[question.id]?.value }
          : tempValues
      };
    });
  };

  const disableSubmit = useMemo(() => {
    const areAnyValuesValid = Object.values(questionValuesMap).some((e) =>
      verifyQuestionValues(e)
    );
    // Submit button is disabled if a request is handled; OR none of the question values are valid
    return isRequestHandled(request) || !areAnyValuesValid;
  }, [questionValuesMap, request]);

  if (questions?.length === 0 || !worker) {
    return null;
  }

  const onArchiveClick = (archiveState: boolean) => {
    if (archiveState) {
      openArchiveRunConfirmModal({
        title: `Archive the Run and Exception`,
        onOk: () => toggleArchive(archiveState, archive?.onSuccess)
      });
    } else {
      toggleArchive(archiveState);
    }
  };

  return (
    <div className={classNames(['run-requests'])}>
      <div className={classNames(['question-group'])}>
        <Layout className="question">
          <Layout.Content>
            <div
              className={classNames('question-group', {
                multiple: questions!.length > 1
              })}
            >
              {questions!.map((question, index) => {
                const isLastQuestion = index + 1 === questions!.length;

                return (
                  <Spin
                    spinning={loadingQuestionIds.has(question.id)}
                    key={question.id}
                  >
                    <RequestQuestion
                      request={request}
                      question={question}
                      worker={worker}
                      procedure={procedure}
                      sentence={sentence}
                      activeDocumentToken={activeDocumentToken}
                      config={questionConfig}
                      answerId={
                        getQuestionAnswer(question, request.answers!)?.id ||
                        undefined
                      }
                      onValuesChange={(values) =>
                        onValuesChange(question, {
                          ...questionValuesMap[question.id],
                          ...values
                        })
                      }
                      toggleAllowSubmit={toggleAllowSubmit}
                      questionValues={questionValuesMap[question.id]}
                    />
                    {!isLastQuestion && (
                      <>
                        <Gutter direction="vertical" size={2} />
                        <Divider
                          style={{
                            margin: 0
                          }}
                        />
                        <Gutter direction="vertical" size={2} />
                      </>
                    )}
                  </Spin>
                );
              })}

              <Gutter size={2} direction="vertical" />
              <Row justify="end" align="middle" gutter={8}>
                <Col>
                  {procedure?.id &&
                    ((!archive?.hideArchiveButton && !worker.isArchived && (
                      <Button
                        loading={loadingQuestionIds.size > 0}
                        size="middle"
                        type="link"
                        data-cy="procedure-run-archive"
                        onClick={() => onArchiveClick(true)}
                      >
                        Archive the run
                      </Button>
                    )) ||
                      (worker.isArchived && (
                        <Button
                          loading={loadingQuestionIds.size > 0}
                          size="middle"
                          type="link"
                          data-cy="procedure-run-unarchive"
                          onClick={() => onArchiveClick(false)}
                        >
                          Unarchive the run
                        </Button>
                      )))}
                </Col>
                {allowSubmit ? (
                  <Col>
                    <Button
                      loading={loadingQuestionIds.size > 0}
                      size="large"
                      type="primary"
                      data-cy="question-submit"
                      onClick={onSubmit}
                      disabled={disableSubmit}
                    >
                      Submit
                    </Button>
                  </Col>
                ) : null}
              </Row>
            </div>
          </Layout.Content>
        </Layout>
      </div>
    </div>
  );
}

export default Request;
